import { reactive, ref, watch } from 'vue'

import { ACTIVITY_CENTER_TAB_NAMES } from '@/constants/activityCenter'

const STORAGE_KEY = 'activity-center-preferences'

const DEFAULT_PREFERENCES = {
  activityNotifications: true,
  failedJobs: true,
  completedJobs: true
}

const loadPreferences = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) } : { ...DEFAULT_PREFERENCES }
  }
  catch {
    return { ...DEFAULT_PREFERENCES }
  }
}

const savePreferences = preferences => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
}

export const shouldShowActivityCenterToast = status => {
  const preferences = loadPreferences()

  if (!preferences.activityNotifications) return false

  if (status === ACTIVITY_CENTER_TAB_NAMES.FAILED) {
    return preferences.failedJobs
  }

  if (status === ACTIVITY_CENTER_TAB_NAMES.COMPLETED) {
    return preferences.completedJobs
  }

  return false
}

export default function useActivityCenterPreferences() {
  const preferences = reactive(loadPreferences())
  const savedChildStates = ref({
    failedJobs: preferences.failedJobs,
    completedJobs: preferences.completedJobs
  })

  watch(
    () => ({ ...preferences }),
    newPreferences => {
      savePreferences(newPreferences)
    },
    { deep: true }
  )

  const toggleActivityNotifications = value => {
    preferences.activityNotifications = value

    if (!value) {
      savedChildStates.value = {
        failedJobs: preferences.failedJobs,
        completedJobs: preferences.completedJobs
      }
      preferences.failedJobs = false
      preferences.completedJobs = false
    }
    else {
      const bothWereOff = !savedChildStates.value.failedJobs
                && !savedChildStates.value.completedJobs

      if (bothWereOff) {
        preferences.failedJobs = true
        preferences.completedJobs = true
        savedChildStates.value = { failedJobs: true, completedJobs: true }
      }
      else {
        preferences.failedJobs = savedChildStates.value.failedJobs
        preferences.completedJobs = savedChildStates.value.completedJobs
      }
    }
  }

  const toggleFailedJobs = value => {
    preferences.failedJobs = value
    savedChildStates.value.failedJobs = value

    if (!value && !preferences.completedJobs) {
      preferences.activityNotifications = false
    }
  }

  const toggleCompletedJobs = value => {
    preferences.completedJobs = value
    savedChildStates.value.completedJobs = value

    if (!value && !preferences.failedJobs) {
      preferences.activityNotifications = false
    }
  }

  return {
    preferences,
    toggleActivityNotifications,
    toggleFailedJobs,
    toggleCompletedJobs
  }
}
